import { trackAnalyticsGrowthbookEvent } from '@ifixit/analytics';
import { type Experiment, GrowthBook, type Result } from '@growthbook/growthbook';
import { type Branded } from '@ifixit/helpers';
import Cookies from 'universal-cookie';
import 'Shared/global.d';

const GROWTHBOOK_DEBUG = new URLSearchParams(window.location.search).get('gbDebug') === 'true';

const canGroupLogs = 'group' in console && 'groupCollapsed' in console && 'groupEnd' in console;

const AB_TEST_PARTICIPANT_COOKIE_NAME = 'ab_participant';
type ABTestParticipantId = Branded<string, 'ABTestParticipantId'>;

const logger = canGroupLogs
   ? /* eslint-disable no-console */
     (msg: string, ctx: Record<string, unknown>) => {
        console.groupCollapsed(`[GrowthBook] ${msg}`);
        console.log(ctx);
        console.groupEnd();
     }
   : (msg: string, ctx: Record<string, unknown>) => {
        console.log(`[GrowthBook] ${msg}`, ctx);
     };
/* eslint-enable no-console */

export async function handleServerExperimentViewed() {
   if (!App.gb) {
      // Growthbook is not enabled on this pagetype. Currently only enabled on CartPage and GuidePage.
      return;
   }

   const growthbook = await getGrowthbook();

   if (App.gb.viewedExperiments) {
      growthbook.setDeferredTrackingCalls(App.gb.viewedExperiments);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME: Promises must be awaited, end with...
      growthbook.fireDeferredTrackingCalls();
   }
}

/**
 * @knipignore
 */
export async function isFeatureOn(feature: string) {
   const growthbook = await getGrowthbook();
   return growthbook.isOn(feature);
}

let growthbookInstance: GrowthBook | null = null;

export async function getGrowthbook() {
   if (growthbookInstance) {
      return growthbookInstance;
   }

   const growthbook = new GrowthBook({
      apiHost: 'https://cdn.growthbook.io',
      clientKey: 'sdk-eWD3DuVIUI93h5o',
      enableDevMode: GROWTHBOOK_DEBUG,
      debug: GROWTHBOOK_DEBUG,
      backgroundSync: true,
      log: logger,
      trackingCallback: (experiment: Experiment<any>, result: Result<any>) => {
         const experimentId = experiment.key;
         const variationId = result.key;

         growthbook.log('Experiment Viewed', {
            experimentId,
            variationId,
            fullDetails: {
               experiment,
               result,
            },
         });

         trackAnalyticsGrowthbookEvent({ experimentId, variationId });
      },
   });

   if (App.gb?.attributes) {
      const uniqueId = getABTestParticipantId();
      await growthbook.setAttributes({
         uniqueId,
         ...App.gb?.attributes,
      });
   }

   if (App.gb?.features) {
      await growthbook.setPayload({
         features: App.gb.features,
      });
   }

   if (App.gb?.forcedFeatures) {
      const featureMap = new Map(Object.entries(App.gb.forcedFeatures));
      await growthbook.setForcedFeatures(featureMap);
   }

   if (App.gb?.forcedVariations) {
      await growthbook.setForcedVariations(App.gb.forcedVariations);
   }

   growthbookInstance = growthbook;

   return growthbook;
}

function getABTestParticipantId(): ABTestParticipantId {
   const cookies = new Cookies();
   const existing = cookies.get(AB_TEST_PARTICIPANT_COOKIE_NAME);
   if (existing) return existing as ABTestParticipantId;

   const id = generateABTestParticipantId() as ABTestParticipantId;
   // Set cookie for 30 days
   cookies.set(AB_TEST_PARTICIPANT_COOKIE_NAME, id, {
      maxAge: 30 * 24 * 60 * 60,
      path: '/',
   });
   return id;
}

/**
 * Generate a random AB test participant ID.
 * Always 11 chars long and looks like: `765zsxykdtk`
 *
 * Chance of collision is: 1 in 36^11 = 1 / 131,621,703,842,267,136
 * For 1 million participants, the chance of collision is about 0.00014%
 */
function generateABTestParticipantId(): string {
   return (
      Math.random()
         // 0-9 and a-z
         .toString(36)
         // Remove the `0.`
         .slice(2)
   );
}

export function AddVercelToolBar() {
   const toolbar = App.toolbar;
   if (!toolbar) {
      return;
   }

   // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME: Promises must be awaited, end with...
   Promise.all([
      import('react-dom/client'),
      import('Shared/react-query-initialize'),
      import('@ifixit/toolbar'),
   ]).then(([{ createRoot }, { initializeReactQuery }, { default: Toolbar }]) => {
      const toolbarWrapper = document.createElement('div');
      document.body.append(toolbarWrapper);

      const reactRoot = createRoot(toolbarWrapper);
      const fetchFeaturesFn = async () => {
         const response = await fetch('/.well-known/ifixit/flags', {
            credentials: 'include',
         });
         return await response.json();
      };

      const domain = location.hostname.split('.').splice(-2).join('.');

      reactRoot.render(
         initializeReactQuery(
            <Toolbar
               fetchFeaturesFn={fetchFeaturesFn}
               cookieName="featureOverrides"
               cookieDomain={'.' + domain}
               header="PHP Feature Flags"
               branch={toolbar.branch}
            />
         )
      );
   });
}
